<script setup lang="ts">
import { computed } from "vue";

import { SiteContext } from "@/app/control/programming/contexts/site-context";
import { useProgrammingDetailsController } from "@/app/control/programming/programming-details.controller";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import BaseLayout from "@/components/layout/base-layout.vue";
import ProgramApplianceStepperCard from "@/components/programming/program-appliance-stepper-card.vue";
import ProgramEditorActions from "@/components/programming/program-editor-actions.vue";
import ProgramIdentityCard from "@/components/programming/program-identity-card.vue";
import ProgramQuickConfigurationsCard from "@/components/programming/program-quick-configurations-card.vue";
import Programs from "@/components/programming/programs.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

interface IProps {
  creationMode?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  creationMode: false,
});

const { t, k } = useTypedIntl();

const computedCreationMode = computed<boolean>(() => !!props.creationMode);

const {
  isProgramSiteDefault,
  breadcrumbFormatter,
  isEditing,
  isUnsavedChangesDialogVisible,
  showNameError,
  siteDetails,
  isSiteDetailsPending,
  isProgramDetailsPending,
  isProgramMutationPending,
  isInstalledApplianceCategoriesPending,
  isInitialized,
  programName,
  programColor,
  subPrograms,
  isNameValid,
  activeApplianceCategory,
  displayedApplianceCategories,
  validatedApplianceCategories,
  activeQuickConfigurations,
  toggleQuickConfiguration,
  handleManualProgramChange,
  primaryActionLabel,
  isPrimaryActionDisabled,
  isPrimaryActionLoading,
  isProgramLoadingError,
  handleRetryProgramLoading,
  handlePrimaryAction,
  handleCancel,
  handleConfirmUnsavedChanges,
} = useProgrammingDetailsController(computedCreationMode);
</script>

<template>
  <SiteContext.Provider
    :value="{
      siteDetails,
      isLoading: isSiteDetailsPending || isInstalledApplianceCategoriesPending,
    }"
  >
    <BaseLayout
      :breadcrumb-formatter="breadcrumbFormatter"
      :is-loading="creationMode ? isInstalledApplianceCategoriesPending : isProgramDetailsPending"
    >
      <template #breadcrumbRight>
        <HospitalityButton
          v-if="isInitialized && !creationMode && !isEditing"
          class="w-[250px] gap-2"
          :data-test-id="DATA_TEST_IDS.components.programEditor.editProgramButton"
          @click="isEditing = true"
        >
          <i
            aria-hidden="true"
            class="pi pi-pencil text-[18px] pr-2"
          />
          {{ t(k.control.programming.details.editProgram) }}
        </HospitalityButton>
      </template>

      <div
        v-if="isProgramLoadingError"
        class="mt-6 flex flex-col items-center gap-4 rounded-xl border border-neutral-light bg-white p-8 text-center"
        role="alert"
      >
        <p class="text-text-secondary">
          {{
            creationMode
              ? t(k.control.programming.details.appliances.loadError)
              : t(k.control.programming.details.loadError)
          }}
        </p>
        <HospitalityButton
          :label="t(k.control.programming.details.retry)"
          @click="handleRetryProgramLoading"
        />
      </div>

      <div
        v-if="isInitialized"
        class="mt-6 flex flex-col gap-6 pb-6"
      >
        <ProgramIdentityCard
          v-model:name="programName"
          v-model:color="programColor"
          :autofocus="creationMode"
          :disabled="isProgramMutationPending"
          :editable="isEditing"
          :invalid="showNameError && !isNameValid"
          :is-program-site-default="isProgramSiteDefault"
          :site-name="siteDetails?.name ?? ''"
          :subtitle="
            creationMode
              ? t(k.control.programming.details.identity.creationSubtitle)
              : t(k.control.programming.details.identity.detailsSubtitle)
          "
        />

        <ProgramApplianceStepperCard
          v-model:active-appliance-category="activeApplianceCategory"
          :appliance-categories="displayedApplianceCategories"
          :creation-mode="creationMode"
          :disabled="isProgramMutationPending"
          :is-editing="isEditing"
          :validated-appliance-categories="validatedApplianceCategories"
        >
          <Programs
            v-model="subPrograms"
            :appliance-category="activeApplianceCategory"
            :editable="isEditing && !isProgramMutationPending"
            :saving="isProgramMutationPending"
            @manual-change="handleManualProgramChange(activeApplianceCategory)"
          />
        </ProgramApplianceStepperCard>

        <ProgramQuickConfigurationsCard
          v-if="isEditing"
          :appliance-category="activeApplianceCategory"
          :active-configurations="activeQuickConfigurations"
          :disabled="isProgramMutationPending"
          @toggle="toggleQuickConfiguration"
        />

        <ProgramEditorActions
          v-if="isEditing"
          :cancel-label="t(k.control.programming.details.cancel)"
          :primary-disabled="isPrimaryActionDisabled"
          :primary-label="primaryActionLabel"
          :primary-loading="isPrimaryActionLoading"
          @cancel="handleCancel"
          @submit="handlePrimaryAction"
        />
      </div>
    </BaseLayout>

    <ConfirmationDialog
      v-model="isUnsavedChangesDialogVisible"
      :callback="handleConfirmUnsavedChanges"
      :cancel-text="t(k.control.programming.details.unsavedChanges.continueEditing)"
      delete-mode
      :submit-text="t(k.control.programming.details.unsavedChanges.discard)"
      :subtitle="t(k.control.programming.details.unsavedChanges.message)"
      :title="t(k.control.programming.details.unsavedChanges.title)"
    />
  </SiteContext.Provider>
</template>
